import type { SimilarSupportTicket } from '@support/entities/support-ticket';
import {
  SupportStatusBadge,
  SupportTypeBadge,
} from '@support/entities/support-ticket';
import { ThumbsUp } from 'lucide-react';
import { joinCss } from '@/shared/lib/css-utils';
import { Text } from '@/shared/ui';
import css from './similar-tickets-list.module.scss';

type SimilarTicketsListProps = {
  items: SimilarSupportTicket[];
  pendingId: string | null;
  onVote: (id: string) => void;
};

// Pure presentational — live search results while the reporter types a
// title. ADVISORY ONLY: this list never gates ticket creation, it just lets
// the reporter upvote an existing ticket instead of filing a duplicate.
// @req SUPPORT-TICKET-003
export const SimilarTicketsList = ({
  items,
  pendingId,
  onVote,
}: SimilarTicketsListProps) => {
  if (items.length === 0) return null;

  return (
    <div className={css.root}>
      <Text as="p" className={css.header} size="xs" variant="muted">
        Similar tickets already reported — upvote instead of duplicating, or
        keep filing yours.
      </Text>
      <ul className={css.list}>
        {items.map((item) => (
          <li className={css.row} key={item.id}>
            <div className={css.info}>
              <Text as="span" className={css.title} size="sm" weight="medium">
                {item.title}
              </Text>
              <div className={css.meta}>
                {/* Gives the reporter something to quote if they decide their
                    report is the same issue as an existing ticket. */}
                {item.ticketRef ? (
                  <Text as="span" className={css.ref} size="xs" variant="muted">
                    {item.ticketRef}
                  </Text>
                ) : null}
                <SupportTypeBadge type={item.type} />
                <SupportStatusBadge status={item.status} />
                <Text as="span" size="xs" variant="muted">
                  {item.createdOn.slice(0, 10)}
                </Text>
              </div>
            </div>
            <button
              aria-label={`Upvote ${item.title}`}
              aria-pressed={item.hasVoted}
              className={joinCss(css.vote, item.hasVoted && css.voteActive)}
              disabled={pendingId === item.id}
              onClick={() => onVote(item.id)}
              type="button"
            >
              <ThumbsUp
                className={css.voteIcon}
                fill={item.hasVoted ? 'currentColor' : 'none'}
              />
              {item.voteCount}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};
